// SoundboardMiniBar — die dezente Leiste ueber der Navigation, die zeigt und
// steuert, was die Klangbuehne gerade spielt. Sie erscheint NUR, solange eine
// Wiedergabe laeuft (auch pausiert), und verschwindet mit dem Stopp.
//
// Sie haengt in App.tsx INNERHALB des Routers, aber AUSSERHALB der AppShell:
// die Vollbild-Chats (/character-chat, /group-chat) liegen bewusst ausserhalb
// der Shell — genau dort verbringt Julius die meiste Zeit, und ohne Leiste
// saehe er weder, was laeuft, noch koennte er es stoppen.
//
// Bewusst klein: Titel (Tippen fuehrt zur Klangbuehne), Play/Pause, Stopp,
// Lautstaerke. Keine Emoji — Icons kommen aus dem SVG-Set (docs 17-code-regeln).
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, IconButton } from '../components/ui';
import { useT } from '../i18n';
import { useOptionalSoundboardPlayer } from './SoundboardPlayerProvider';
import styles from './SoundboardMiniBar.module.css';

/** Gemessene Hoehe der Leiste — Seiten reservieren damit Platz (PageContainer). */
const INSET_VAR = '--lorath-soundboard-inset';

export function SoundboardMiniBar() {
  const { t } = useT();
  const navigate = useNavigate();
  const player = useOptionalSoundboardPlayer();
  const barRef = useRef<HTMLDivElement | null>(null);

  // Ohne Provider (isolierte Shell-Tests) gibt es nichts zu zeigen. Waehrend
  // ein Live-Gespraech die Klangbuehne verdraengt, verschwindet die Leiste
  // ebenfalls — sonst boete sie einen Knopf an, der nichts hoerbar macht.
  const visible = Boolean(player?.active && player.track && !player.suspended);

  // Muster von CookieConsent: die Leiste liegt position:fixed am unteren Rand
  // und verdeckte sonst die letzte Zeile jeder Seite (auf Laptop/Tablet die
  // unterste Kachelreihe, in compact zusaetzlich den Offline-Hinweis). Ihre
  // GEMESSENE Hoehe steht deshalb als CSS-Variable am <html>; ohne Leiste ist
  // sie nicht gesetzt (Fallback 0px). Gemessen statt geschaetzt, weil die
  // Leiste je nach Sprache und Breite unterschiedlich hoch laeuft.
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.removeProperty(INSET_VAR);
      return undefined;
    }
    const node = barRef.current;
    if (!node) return undefined;
    // Border-Box messen (getBoundingClientRect), NICHT contentRect: Polster und
    // Rahmen der Leiste gehoeren zur belegten Hoehe.
    const write = () => {
      root.style.setProperty(INSET_VAR, `${Math.ceil(node.getBoundingClientRect().height)}px`);
    };
    write();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(write) : null;
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(INSET_VAR);
    };
  }, [visible]);

  if (!visible || !player?.track) return null;
  const { playing, track, volume, pause, resume, stop, setVolume } = player;

  return (
    <div ref={barRef} className={styles.bar} role="region" aria-label={t('soundboard.now_playing')}>
      <button
        type="button"
        className={styles.title}
        title={t('soundboard.open')}
        onClick={() => navigate('/soundboard')}
      >
        <Icon name="music-radio" size={20} className={styles.titleIcon} />
        <span className={styles.titleText}>{track.title || t('soundboard.title')}</span>
      </button>
      <IconButton
        icon={playing ? 'pause' : 'play'}
        aria-label={playing ? t('soundboard.pause') : t('soundboard.play')}
        onClick={() => (playing ? pause() : resume())}
      />
      <IconButton icon="stop" aria-label={t('soundboard.stop')} onClick={stop} />
      <span className={styles.volume}>
        <Icon name="volume" size={18} />
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={volume}
          aria-label={t('soundboard.volume')}
          onChange={(event) => setVolume(Number(event.target.value))}
        />
      </span>
    </div>
  );
}
